import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAuth } from '../contexts/AuthContext';
import { useSEO } from '../hooks/useSEO';

const RegisterPage = () => {
  useSEO({
    title: "Create Your Account | AM CARDMATE",
    description: "Join AM CARDMATE today! Create your free account and start building professional digital business cards.",
    keywords: "register, sign up, create account, join, new user"
  });

  useEffect(() => {
    const schemas = [
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "Create AM CARDMATE Account", 
        "description": "Access your digital business cards and manage your professional network",
        "url": "https://amcardmate.com/signup", 
        "mainEntity": {
          "@type": "WebApplication",
          "name": "AM CARDMATE",
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "Web Browser",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "GBP"
          }
        }
      },
      {
        "@context": "https://schema.org",
        "@type": "Action",
        "name": "User Authentication",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": "https://amcardmate.com/account",
          "actionPlatform": [
            "http://schema.org/DesktopWebPlatform",
            "http://schema.org/MobileWebPlatform"
          ]
        }
      }
    ];
    
    schemas.forEach(schema => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });
  }, []);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, feedback: '' });
  const [acceptTerms, setAcceptTerms] = useState(false);
  
  const { register, signInWithGoogle, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated()) {
      navigate('/account', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const calculatePasswordStrength = (password) => {
    let score = 0;
    let feedback = [];

    if (password.length >= 8) score += 1;
    else feedback.push('at least 8 characters');

    if (/[a-z]/.test(password)) score += 1;
    else feedback.push('lowercase letters');

    if (/[A-Z]/.test(password)) score += 1;
    else feedback.push('uppercase letters');

    if (/[0-9]/.test(password)) score += 1;
    else feedback.push('numbers');

    if (/[^A-Za-z0-9]/.test(password)) score += 1;
    else feedback.push('special characters');

    const strengthLevels = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong'];
    const colors = ['text-red-600', 'text-orange-600', 'text-yellow-600', 'text-blue-600', 'text-green-600'];
    
    return {
      score,
      label: strengthLevels[Math.min(score, 4)],
      color: colors[Math.min(score, 4)],
      feedback: feedback.length > 0 ? `Add ${feedback.join(', ')}` : 'Strong password!'
    };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    if (name === 'password') {
      setPasswordStrength(calculatePasswordStrength(value));
    }

    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    } else if (formData.firstName.trim().length < 2) {
      newErrors.firstName = 'First name must be at least 2 characters';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    } else if (formData.lastName.trim().length < 2) {
      newErrors.lastName = 'Last name must be at least 2 characters';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (formData.phone && !/^[\+]?[1-9][\d]{0,15}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    } else if (passwordStrength.score < 2) {
      newErrors.password = 'Please choose a stronger password';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!acceptTerms) {
      newErrors.terms = 'You must accept the terms and conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const { user, error } = await register(formData.email, formData.password, {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        phone: formData.phone.trim(),
        company: formData.company.trim()
      });
      
      if (user && !error) {
        navigate('/account');
      } else if (error) {
        setErrors({ general: error });
      }
    } catch (error) {
      setErrors({ general: 'An unexpected error occurred. Please try again.' });
    }
    
    setLoading(false);
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    setErrors({});

    try {
      const { user, error } = await signInWithGoogle();
      
      if (user && !error) {
        navigate('/account');
      } else if (error) {
        setErrors({ general: error });
      }
    } catch (error) {
      setErrors({ general: 'An unexpected error occurred. Please try again.' });
    }
    
    setGoogleLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
      <div className="absolute top-0 right-0 w-[300px] sm:w-[400px] md:w-[500px] h-[300px] sm:h-[400px] md:h-[500px] bg-gradient-to-br from-blue-200/20 to-purple-200/20 rounded-full filter blur-3xl" />
      <div className="absolute bottom-0 left-0 w-[250px] sm:w-[300px] md:w-[400px] h-[250px] sm:h-[300px] md:h-[400px] bg-gradient-to-tr from-purple-200/20 to-pink-200/20 rounded-full filter blur-3xl" />

      <div className="max-w-md w-full space-y-8 relative z-10">
        <div className="text-center">
          <Link to="/" className="inline-flex items-center gap-3 mb-6 group">
            <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl text-white shadow-lg group-hover:shadow-xl transition-all duration-300">
              <FontAwesomeIcon icon="qrcode" className="text-xl" />
            </div>
            <span className="text-2xl font-extrabold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">AM CARDMATE</span>
          </Link>
          
          <h2 className="text-h3 text-gray-900">
            Create your account
          </h2>
          <p className="mt-2 text-body text-gray-600">
            Join thousands of professionals using smart NFC cards
          </p>
        </div>

        <div className="bg-white/70 backdrop-blur-sm rounded-3xl shadow-2xl p-6 sm:p-8">
          {errors.general && (
            <div className="mb-4 p-3 bg-red-50 rounded-2xl">
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon="exclamation-triangle" className="text-red-500" />
                <p className="text-body-sm text-red-600">{errors.general}</p>
              </div>
            </div>
          )}

          <div className="mb-6">
            <button 
              onClick={handleGoogleSignIn}
              disabled={loading || googleLoading}
              className="w-full py-3 px-4 rounded-2xl shadow-sm bg-white text-btn text-gray-700 hover:bg-gray-50 transition-all duration-300 border border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-md flex items-center justify-center"
            >
              {googleLoading ? (
                <FontAwesomeIcon icon="spinner" className="animate-spin mr-2" />
              ) : (
                <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              )}
              <span>Continue with Google</span>
            </button>
          </div>

          <div className="mb-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center">
                <span className="px-2 bg-white text-body-sm text-gray-500">Or sign up with email</span>
              </div>
            </div>
          </div>

          <form className="space-y-4 sm:space-y-6" onSubmit={handleSubmit}>
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label htmlFor="firstName" className="block text-label text-gray-700 mb-1">
                  First Name *
                </label>
                <input
                  id="firstName"
                  name="firstName"
                  type="text"
                  required
                  value={formData.firstName}
                  onChange={handleChange}
                  className={`appearance-none relative block w-full px-3 sm:px-4 py-3 bg-gray-50 placeholder-gray-500 text-gray-900 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all duration-300 text-body ${
                    errors.firstName ? 'ring-2 ring-red-300' : ''
                  }`}
                  placeholder="John"
                />
                {errors.firstName && <p className="mt-1 text-caption text-red-600">{errors.firstName}</p>}
              </div>

              <div>
                <label htmlFor="lastName" className="block text-label text-gray-700 mb-1">
                  Last Name *
                </label>
                <input
                  id="lastName"
                  name="lastName"
                  type="text"
                  required
                  value={formData.lastName}
                  onChange={handleChange}
                  className={`appearance-none relative block w-full px-3 sm:px-4 py-3 bg-gray-50 placeholder-gray-500 text-gray-900 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all duration-300 text-body ${
                    errors.lastName ? 'ring-2 ring-red-300' : ''
                  }`}
                  placeholder="Doe"
                />
                {errors.lastName && <p className="mt-1 text-caption text-red-600">{errors.lastName}</p>}
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-label text-gray-700 mb-1">
                Email Address *
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none">
                  <FontAwesomeIcon icon="envelope" className="text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className={`appearance-none relative block w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-3 bg-gray-50 placeholder-gray-500 text-gray-900 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all duration-300 text-body ${
                    errors.email ? 'ring-2 ring-red-300' : ''
                  }`}
                  placeholder="john@example.com"
                />
              </div>
              {errors.email && <p className="mt-1 text-caption text-red-600">{errors.email}</p>}
            </div>

            <div>
              <label htmlFor="phone" className="block text-label text-gray-700 mb-1">
                Phone Number
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none">
                  <FontAwesomeIcon icon="phone" className="text-gray-400" />
                </div>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  className={`appearance-none relative block w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-3 bg-gray-50 placeholder-gray-500 text-gray-900 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all duration-300 text-body ${
                    errors.phone ? 'ring-2 ring-red-300' : ''
                  }`}
                  placeholder="+44 123 456 7890"
                />
              </div>
              {errors.phone && <p className="mt-1 text-caption text-red-600">{errors.phone}</p>}
            </div>

            <div>
              <label htmlFor="company" className="block text-label text-gray-700 mb-1">
                Company
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none">
                  <FontAwesomeIcon icon="building" className="text-gray-400" />
                </div>
                <input
                  id="company"
                  name="company"
                  type="text"
                  value={formData.company}
                  onChange={handleChange}
                  className="appearance-none relative block w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-3 bg-gray-50 placeholder-gray-500 text-gray-900 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all duration-300 text-body"
                  placeholder="Your Company"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-label text-gray-700 mb-1">
                Password *
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none">
                  <FontAwesomeIcon icon="lock" className="text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className={`appearance-none relative block w-full pl-10 sm:pl-12 pr-10 sm:pr-12 py-3 bg-gray-50 placeholder-gray-500 text-gray-900 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all duration-300 text-body ${
                    errors.password ? 'ring-2 ring-red-300' : ''
                  }`}
                  placeholder="Create a strong password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 sm:pr-4 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  <FontAwesomeIcon 
                    icon={showPassword ? "eye-slash" : "eye"} 
                    className="text-gray-400 hover:text-gray-600 transition-colors" 
                  />
                </button>
              </div>
              
              {formData.password && (
                <div className="mt-2">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full transition-all duration-300 ${
                          passwordStrength.score >= 4 ? 'bg-green-500' :
                          passwordStrength.score >= 3 ? 'bg-blue-500' :
                          passwordStrength.score >= 2 ? 'bg-yellow-500' :
                          passwordStrength.score >= 1 ? 'bg-orange-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${(passwordStrength.score / 5) * 100}%` }}
                      />
                    </div>
                    <span className={`text-caption font-medium ${passwordStrength.color}`}>
                      {passwordStrength.label}
                    </span>
                  </div>
                  <p className="text-caption text-gray-500">{passwordStrength.feedback}</p>
                </div>
              )}
              
              {errors.password && <p className="mt-1 text-caption text-red-600">{errors.password}</p>}
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-label text-gray-700 mb-1">
                Confirm Password *
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none">
                  <FontAwesomeIcon icon="lock" className="text-gray-400" />
                </div>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  required
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className={`appearance-none relative block w-full pl-10 sm:pl-12 pr-10 sm:pr-12 py-3 bg-gray-50 placeholder-gray-500 text-gray-900 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all duration-300 text-body ${
                    errors.confirmPassword ? 'ring-2 ring-red-300' : ''
                  }`}
                  placeholder="Confirm your password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 sm:pr-4 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  <FontAwesomeIcon 
                    icon={showConfirmPassword ? "eye-slash" : "eye"} 
                    className="text-gray-400 hover:text-gray-600 transition-colors" 
                  />
                </button>
              </div>
              {errors.confirmPassword && <p className="mt-1 text-caption text-red-600">{errors.confirmPassword}</p>}
            </div>

            <div className="flex items-start gap-3">
              <input
                id="acceptTerms"
                name="acceptTerms"
                type="checkbox"
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
                className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 rounded"
              />
              <div className="flex-1">
                <label htmlFor="acceptTerms" className="text-body-sm text-gray-600">
                  I agree to the{' '}
                  <Link to="/terms" className="text-blue-600 hover:text-blue-500 font-medium">
                    Terms of Service
                  </Link>
                  {' '}and{' '}
                  <Link to="/privacy" className="text-blue-600 hover:text-blue-500 font-medium">
                    Privacy Policy
                  </Link>
                </label>
                {errors.terms && <p className="mt-1 text-caption text-red-600">{errors.terms}</p>}
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading || googleLoading || !acceptTerms}
                className="w-full py-3 px-4 text-btn rounded-2xl text-white bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center"
              >
                {loading ? (
                  <FontAwesomeIcon icon="spinner" className="animate-spin" />
                ) : (
                  <>
                    <FontAwesomeIcon icon="user-plus" className="mr-2" />
                    <span>Create Account</span>
                  </>
                )}
              </button>
            </div>
          </form>

          <div className="mt-6 text-center">
            <p className="text-body text-gray-600">
              Already have an account?{' '}
              <Link 
                to="/login" 
                className="font-medium text-blue-600 hover:text-blue-500 transition-colors"
              >
                Sign in here
              </Link>
            </p>
          </div>
        </div>

        <div className="bg-white/50 backdrop-blur-sm rounded-2xl p-4 sm:p-6">
          <h3 className="text-h5 text-gray-900 mb-3">What you get:</h3>
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-body-sm text-gray-600">
              <FontAwesomeIcon icon="credit-card" className="text-blue-500" />
              <span>Access to premium NFC cards</span>
            </div>
            <div className="flex items-center gap-2 text-body-sm text-gray-600">
              <FontAwesomeIcon icon="chart-line" className="text-purple-500" />
              <span>Track your networking analytics</span>
            </div>
            <div className="flex items-center gap-2 text-body-sm text-gray-600">
              <FontAwesomeIcon icon="mobile-alt" className="text-green-500" />
              <span>Manage your digital profile</span>
            </div>
            <div className="flex items-center gap-2 text-body-sm text-gray-600">
              <FontAwesomeIcon icon="users" className="text-indigo-500" />
              <span>Join our professional network</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;